import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { AllocationRuleStore } from '@yard/stores/allocation-rule.store'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useYardSetupMessageHandler } from '../../hooks/useYardSetupMessageHandler'
import { IFlexibleBlockFormData } from '../../models/yard-setup.model'
import { FlexibleBlockViewStore } from '../../stores/nodes/flexible-block.view-store'
import { FlexibleBlockForm } from '../molecules/FlexibleBlockForm'

interface Props {
  flexibleBlockViewStore: FlexibleBlockViewStore
  allocationRuleStore: AllocationRuleStore
}

export const FlexibleBlockDialogs = observer(
  ({ flexibleBlockViewStore, allocationRuleStore }: Props) => {
    const { t } = useTranslate()
    const { getExceptionMessage, getFlexibleSuccessMessage, getFlexibleDeleteSuccessMessage } =
      useYardSetupMessageHandler()

    const hasAllocationRules = useMemo(() => {
      if (!flexibleBlockViewStore.editingId) return false
      return allocationRuleStore.items.some(rule =>
        rule.destinations.some(dest => dest.blockId === flexibleBlockViewStore.editingId),
      )
    }, [allocationRuleStore.items, flexibleBlockViewStore.editingId])

    const onSubmit = async (data: IFlexibleBlockFormData) => {
      try {
        await flexibleBlockViewStore.submit(data)
        appStore.setShowAlert(
          'success',
          getFlexibleSuccessMessage(!!flexibleBlockViewStore.editingId),
        )
        flexibleBlockViewStore.closeFormDialog()
      } catch (e: any) {
        appStore.setShowAlert('error', getExceptionMessage(e))
      }
    }

    const onDelete = async () => {
      try {
        await flexibleBlockViewStore.delete(flexibleBlockViewStore.editingId!)
        appStore.setShowAlert('success', getFlexibleDeleteSuccessMessage())
        flexibleBlockViewStore.setDeleteDialogOpen()
      } catch (e: any) {
        appStore.setShowAlert('error', getExceptionMessage(e))
      }
    }

    return (
      <>
        <ConfirmationDialog
          open={flexibleBlockViewStore.isFormDialogOpen}
          title={
            flexibleBlockViewStore.focusedFlexibleBlock
              ? t('editFlexibleBlock', 'Edit flexible block')
              : t('addFlexibleBlock', 'Add flexible block')
          }
          primaryActionText={
            flexibleBlockViewStore.focusedFlexibleBlock
              ? t('yesUpdate', 'Yes, update')
              : t('yesCreate', 'Yes, create')
          }
          closeLabel={t('noCancel', 'No, cancel')}
          formId='flexible-block-form'
          maxWidth='sm'
          onClose={() => flexibleBlockViewStore.closeFormDialog()}
        >
          <FlexibleBlockForm
            id='flexible-block-form'
            flexibleBlock={flexibleBlockViewStore.focusedFlexibleBlock}
            onSubmit={onSubmit}
          />
        </ConfirmationDialog>

        <ConfirmationDialog
          open={flexibleBlockViewStore.isDeleteDialogOpen}
          title={t('deleteFlexibleBlock', 'Delete flexible block')}
          message={t(
            'deleteFlexibleBlockAllocationRuleWarning',
            'This flexible block has allocation rules assigned to it. Deleting it will remove those assignments.',
          )}
          primaryActionText={t('yesDelete', 'Yes, delete')}
          closeLabel={t('noCancel', 'No, cancel')}
          onClose={() => flexibleBlockViewStore.setDeleteDialogOpen()}
          onConfirm={onDelete}
          maxWidth='xs'
        />
      </>
    )
  },
)
